import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import axios, { type AxiosError } from "axios";
import clearCookies from "../../utils/clearCookies";
import { getAPIKeys, getURLPrefix } from "../../utils/getUrlPrefix";
import { isNetworkError } from "../../utils/isNetworkError";
import { resetAmzOnboarding } from "./amzOnboardReducer";
import * as Sentry from "@sentry/react";
import { setChannel } from "../reducers/channel";
import { setMarketPlace } from "../reducers/marketPlace";
import { setAuthLoading } from "../reducers/authReducers";
import type { AppDispatch } from "../store";
import type { Module } from "../../utils/modules";
import { queryClient } from "../../config/react-query";
import type { UserProfile } from "../../zodios/apiDefinitions/api/userProfile";
import { setAuthTokens } from "../../modules/auth/tokens/tokens";
import { posthog } from "../../config/posthog";

const initialState = {
  user_full_name: "",
  organization_admin: false,
  organization_name: "",
  organization_id: "",
  organization_type: "" as string | null,
  organization_created_date: "",
  icp_status: null as string | null,
  current_profile: "",
  agreementStatus: "",
  tier: "",
  pricing_tier: "",
  analytics_tier: "",
  content_tier: "",
  promotions_tier: "",
  pb_tier: "",
  brand_registered: "",
  allowMwsOrders: false,
  productIdentifier: "",
  preferences: {
    acos_or_roas: "acos" as "acos" | "roas",
    all_notifications: false as boolean | undefined,
    merchandise_dashboard: {} as Record<string, boolean>,
    ad_product_out_of_stock: false as boolean | undefined,
    ad_product_low_in_stock: false as boolean | undefined,
    product_ineligibility: false as boolean | undefined,
    ad_kw_asin_notif: false as boolean | undefined,
    product_exceed_spend: false as boolean | undefined,
    cogs: "classic" as "classic" | "landed_cost" | undefined,
  },
  modules: [] as Module[],
  is_staff: false,
  user_id: "",
  tos_acceptance_date: null as string | null,
  total_sales_start_date: null as Record<string, string> | null,
  ad_count: 0,
  email: null as string | null,
  email_verified_on: null as string | null,
  customer_type: null as string | null,
  annual_amazon_sales: null as string | null,
  total_monthly_sales: null as string | null,
  defaultMarketplace: {} as Record<string, string> | null,
  landedCostPer: undefined as unknown,
  shippingCostPer: undefined as unknown,
  prefers_org_name_display: false,
  digital_shelf_limit: 0,
  account_manager: {
    id: null as number | null,
    agency_id: null as number | null,
  },
  initial_data_loaded_on: null as string | null,
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setProductIndentifier: (state, action: PayloadAction<string>) => {
      state.productIdentifier = action.payload;
    },
    setDefaultMarketPlace(state, action: PayloadAction<{ orgId: string; marketplace: string }>) {
      if (!state.defaultMarketplace) {
        state.defaultMarketplace = {};
      }
      state.defaultMarketplace[action.payload.orgId] = action.payload.marketplace;
    },
    setPerformanceMetric(state, action: PayloadAction<"acos" | "roas">) {
      state.preferences.acos_or_roas = action.payload;
    },
    setSubscriptionTiers(state, action: PayloadAction<Partial<typeof initialState>>) {
      state = {
        ...state,
        ...action.payload,
      };
      return state;
    },
    unsetCurrentUser: () => {
      return initialState;
    },
    setCurrentUser: (state, action: PayloadAction<Partial<UserProfile>>) => {
      if (action.payload.full_user_name !== undefined) state.user_full_name = action.payload.full_user_name;
      if (action.payload.current_profile !== undefined) {
        state.current_profile = action.payload.current_profile;
      }
      if (action.payload.is_organization_admin !== undefined)
        state.organization_admin = action.payload.is_organization_admin;
      if (action.payload.organization_type !== undefined) state.organization_type = action.payload.organization_type;
      if (action.payload.organization_name !== undefined) state.organization_name = action.payload.organization_name;
      if (action.payload.organization_id !== undefined) state.organization_id = action.payload.organization_id;
      if (action.payload.organization_created_date !== undefined)
        state.organization_created_date = action.payload.organization_created_date;
      if (action.payload.icp_status !== undefined) state.icp_status = action.payload.icp_status;
      if (action.payload.latest_tos_agreement_status !== undefined)
        state.agreementStatus = action.payload.latest_tos_agreement_status;
      if (action.payload.tier !== undefined) state.tier = action.payload.tier;
      if (action.payload.pricing_tier !== undefined) state.pricing_tier = action.payload.pricing_tier;
      if (action.payload.analytics_tier !== undefined) state.analytics_tier = action.payload.analytics_tier;
      if (action.payload.content_tier !== undefined) state.content_tier = action.payload.content_tier;
      if (action.payload.promotions_tier !== undefined) state.promotions_tier = action.payload.promotions_tier;
      if (action.payload.pb_tier !== undefined) state.pb_tier = action.payload.pb_tier;
      if (action.payload.brand_registered !== undefined) state.brand_registered = action.payload.brand_registered;
      if (action.payload.product_identifier !== undefined) state.productIdentifier = action.payload.product_identifier;
      if (action.payload.modules !== undefined) state.modules = action.payload.modules;
      if (action.payload.preferences !== undefined)
        state.preferences = {
          ...state.preferences,
          ...action.payload.preferences,
        };
      if (action.payload.default_marketplace !== undefined)
        state.defaultMarketplace = action.payload.default_marketplace;
      if (action.payload.is_staff !== undefined) state.is_staff = action.payload.is_staff;
      if (action.payload.user_id !== undefined) state.user_id = action.payload.user_id;
      if (action.payload.tos_acceptance_date !== undefined)
        state.tos_acceptance_date = action.payload.tos_acceptance_date;
      if (action.payload.total_sales_start_date !== undefined)
        state.total_sales_start_date = action.payload.total_sales_start_date;
      if (action.payload.ad_count !== undefined) state.ad_count = action.payload.ad_count;
      if (action.payload.email !== undefined) state.email = action.payload.email;
      if (action.payload.email_verified_on !== undefined) state.email_verified_on = action.payload.email_verified_on;
      if (action.payload.customer_type !== undefined) state.customer_type = action.payload.customer_type;
      if (action.payload.annual_amazon_sales !== undefined)
        state.annual_amazon_sales = action.payload.annual_amazon_sales;
      if (action.payload.total_monthly_sales !== undefined)
        state.total_monthly_sales = action.payload.total_monthly_sales;
      if (action.payload.prefers_org_name_display !== undefined)
        state.prefers_org_name_display = action.payload.prefers_org_name_display;
      if (action.payload.digital_shelf_limit !== undefined)
        state.digital_shelf_limit = action.payload.digital_shelf_limit;
      if (action.payload.account_manager !== undefined) state.account_manager = action.payload.account_manager;
      if (action.payload.initial_data_loaded_on !== undefined)
        state.initial_data_loaded_on = action.payload.initial_data_loaded_on;

      Sentry.setUser({
        id: action.payload.user_id,
        email: action.payload.email,
        username: action.payload.full_user_name,
      });
      Sentry.setTags({
        org: action.payload.organization_id || "",
      });

      if (action.payload.user_id) {
        posthog.identify(action.payload.user_id, {
          email: action.payload.email,
          name: action.payload.full_user_name,
          organization_id: action.payload.organization_id,
          organization_name: action.payload.organization_name,
          is_staff: action.payload.is_staff,
        });
      }

      window._hsq = window._hsq || [];
      window._hsq.push([
        "identify",
        {
          email: action.payload.email,
        },
      ]);
    },
  },
});

export const {
  setProductIndentifier,
  setDefaultMarketPlace,
  setPerformanceMetric,
  setSubscriptionTiers,
  unsetCurrentUser,
  setCurrentUser,
} = userSlice.actions;
export default userSlice.reducer;

const apiKeys = getAPIKeys();
const CLIENT_ID = apiKeys["API_AUTH_CLIENT_ID"];
const URL_PREFIX = getURLPrefix();

export const logoutUser = () => (dispatch: AppDispatch) => {
  const userInfo = {
    client_id: CLIENT_ID,
  };

  axios
    .post(URL_PREFIX + "/logout/", userInfo)
    .catch((err: AxiosError) => {
      if (err.response?.status !== 401 && !isNetworkError(err)) {
        Sentry.captureException(err);
      }
    })
    .finally(() => {
      dispatch(setAuthTokens(null));
      dispatch(unsetCurrentUser());
      dispatch(setChannel("None"));
      dispatch(setAuthLoading(false));
      dispatch(setMarketPlace("None"));
      dispatch(resetAmzOnboarding());

      sessionStorage.clear();

      let keysToKeep = [
        "Marketplace",
        "extensionSignup",
        "tableSettings",
        "columnOptions",
        "metricOptions",
        "dismiss_chrome_extension_banner",
        "dismiss_chrome_extension_help_banner",
        "dismiss_invalid_amazon_payment_method_banner",
        "dismiss_sp_api_credential_expiration_banner",
        "temp-goals",
      ];

      const tableStateKeys = Object.keys(localStorage).filter((key) => key.startsWith("tableState"));
      keysToKeep = keysToKeep.concat(tableStateKeys);

      const mapKeysToKeep = {} as Record<(typeof keysToKeep)[number], string>;

      for (const key of keysToKeep) {
        const value = localStorage.getItem(key);
        if (value && !["null", "undefined"].includes(value)) {
          mapKeysToKeep[key] = value;
        }
      }

      localStorage.clear();

      for (const key of Object.keys(mapKeysToKeep)) {
        localStorage.setItem(key, mapKeysToKeep[key]!);
      }

      queryClient.clear();

      clearCookies();
      Sentry.setUser(null);
      posthog.reset();
    });
};
